// /Frontend/hoppla-admin/src/lib/api/http.ts

import {
  clearStoredAdminSession,
  readStoredAdminSession,
  writeStoredAdminSession,
  type StoredAdminSession,
} from '../../features/auth/auth-storage';
import { env } from '../env';

const API_BASE_URL = env.apiBaseUrl;
const REQUEST_TIMEOUT_MS = 15000;

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export interface RequestOptions {
  auth?: boolean;
  idempotency?: boolean;
  retryOnUnauthorized?: boolean;
  timeoutMs?: number;
}

let refreshPromise: Promise<StoredAdminSession | null> | null = null;

function getCookieValue(name: string): string {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }

  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));

  if (!cookie) {
    return '';
  }

  const rawValue = cookie.slice(name.length + 1);

  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
}

function ensureApiBaseUrl(): void {
  if (!API_BASE_URL) {
    throw new Error(
      "VITE_API_BASE_URL est manquant. Impossible d'exécuter la requête API.",
    );
  }
}

function normalizePath(path: string): string {
  const value = String(path ?? '').trim();

  if (!value.startsWith('/')) {
    throw new Error("Le chemin API doit commencer par '/'.");
  }

  if (/^https?:\/\//i.test(value)) {
    throw new Error(
      "Les URL absolues ne sont pas autorisées dans le client API.",
    );
  }

  return value;
}

function buildIdempotencyKey(): string {
  const bytes = new Uint8Array(16);

  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  const randomHex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  return `${Date.now()}-${randomHex}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function readMessageCandidate(value: unknown): string | null {
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized ? normalized : null;
  }

  if (Array.isArray(value)) {
    const parts = value
      .filter((item): item is string => typeof item === 'string')
      .map((item) => item.trim())
      .filter(Boolean);

    return parts.length ? parts.join(' · ') : null;
  }

  return null;
}

function extractMessage(data: unknown): string {
  const root = asRecord(data);
  const nestedError = asRecord(root?.error);

  const candidates: unknown[] = [
    root?.message,
    nestedError?.message,
    root?.error_description,
    nestedError?.error_description,
    typeof data === 'string' ? data : null,
  ];

  for (const candidate of candidates) {
    const message = readMessageCandidate(candidate);
    if (message) {
      return message;
    }
  }

  return 'La requête API a échoué.';
}

function normalizeAuthResponse(
  raw: Record<string, unknown>,
  fallback?: StoredAdminSession | null,
): StoredAdminSession {
  const rawAdmin = asRecord(raw.admin) ?? fallback?.admin ?? null;

  const admin =
    rawAdmin &&
    typeof rawAdmin.id === 'string' &&
    typeof rawAdmin.username === 'string' &&
    typeof rawAdmin.role === 'string'
      ? {
          id: rawAdmin.id,
          username: rawAdmin.username,
          role: rawAdmin.role as StoredAdminSession['admin']['role'],
        }
      : undefined;

  const rawTokens = asRecord(raw.tokens);

  const rawSession = asRecord(raw.session);
  const sessionId =
    (typeof raw.sessionId === 'string' ? raw.sessionId : undefined) ??
    (typeof rawSession?.id === 'string' ? rawSession.id : undefined) ??
    fallback?.sessionId ??
    null;

  if (!admin?.id) {
    throw new Error('Réponse de refresh invalide.');
  }

  return {
    admin,
    sessionId,
  };
}

async function parseResponseBody<T>(
  response: Response,
): Promise<T | unknown | null> {
  if (response.status === 204) {
    return null;
  }

  const contentType = (response.headers.get('content-type') ?? '').toLowerCase();

  if (contentType.includes('application/json')) {
    return (await response.json().catch(() => null)) as T | unknown | null;
  }

  const text = await response.text().catch(() => '');

  if (!text.trim()) {
    return null;
  }

  return text;
}

async function performFetch(
  input: string,
  init: RequestInit,
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    timeoutMs,
  );

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
      mode: 'cors',
      cache: 'no-store',
      credentials: 'include',
      redirect: 'error',
      referrerPolicy: 'no-referrer',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('Délai dépassé. Veuillez réessayer.', 408, null);
    }

    throw new ApiError(
      'Connexion impossible au serveur ou requête CORS bloquée.',
      0,
      null,
    );
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function refreshAdminSession(): Promise<StoredAdminSession | null> {
  const current = readStoredAdminSession();

  if (!current?.sessionId) {
    clearStoredAdminSession();
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        ensureApiBaseUrl();

        const response = await performFetch(`${API_BASE_URL}/admin/auth/refresh`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(getCookieValue('hoppla_admin_csrf')
              ? { 'X-CSRF-Token': getCookieValue('hoppla_admin_csrf') }
              : {}),
          },
          body: JSON.stringify({}),
        });

        const data =
          (await parseResponseBody<Record<string, unknown>>(response)) ?? {};

        if (!response.ok) {
          clearStoredAdminSession();
          return null;
        }

        const normalized = normalizeAuthResponse(asRecord(data) ?? {}, current);
        writeStoredAdminSession(normalized);
        return normalized;
      } catch {
        clearStoredAdminSession();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

export async function request<T>(
  path: string,
  init: RequestInit,
  options: RequestOptions = {},
): Promise<T> {
  ensureApiBaseUrl();

  const safePath = normalizePath(path);
  const {
    auth = true,
    idempotency = false,
    retryOnUnauthorized = true,
    timeoutMs = REQUEST_TIMEOUT_MS,
  } = options;

  const headers = new Headers(init.headers ?? {});
  const csrfToken = getCookieValue('hoppla_admin_csrf');

  headers.set('Accept', 'application/json');
  headers.set('X-Requested-With', 'XMLHttpRequest');
  if (csrfToken && !headers.has('X-CSRF-Token')) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (idempotency && !headers.has('X-Idempotency-Key')) {
    headers.set('X-Idempotency-Key', buildIdempotencyKey());
  }

  const response = await performFetch(`${API_BASE_URL}${safePath}`, {
    ...init,
    headers,
  }, timeoutMs);

  const data = await parseResponseBody<T>(response);

  if (response.status === 401 && auth && retryOnUnauthorized) {
    const refreshed = await refreshAdminSession();

    if (refreshed?.sessionId) {
      return request<T>(safePath, init, {
        ...options,
        retryOnUnauthorized: false,
      });
    }

    clearStoredAdminSession();
  }

  if (!response.ok) {
    throw new ApiError(extractMessage(data), response.status, data);
  }

  return data as T;
}

export async function apiGet<T>(
  path: string,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(path, { method: 'GET' }, options);
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(
    path,
    {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

/** Reuse a caller-owned key after a timeout so a second click cannot create another project. */
export async function apiPostWithKey<T>(path: string, body: unknown, key: string): Promise<T> {
  if (!/^[A-Za-z0-9._:-]{16,120}$/.test(key)) throw new Error('Clé d’idempotence invalide.');
  return request<T>(path, { method: 'POST', headers: { 'X-Idempotency-Key': key }, body: JSON.stringify(body) });
}

export async function apiPatch<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    options,
  );
}

export async function apiDelete<T>(
  path: string,
  options?: RequestOptions,
): Promise<T> {
  return request<T>(path, { method: 'DELETE' }, options);
}

export async function requestBlob(
  path: string,
  init: RequestInit,
  options: RequestOptions = {},
): Promise<Blob> {
  ensureApiBaseUrl();

  const safePath = normalizePath(path);
  const {
    auth = true,
    idempotency = false,
    retryOnUnauthorized = true,
  } = options;

  const headers = new Headers(init.headers ?? {});
  const csrfToken = getCookieValue('hoppla_admin_csrf');

  headers.set('Accept', 'application/pdf,application/octet-stream;q=0.9,*/*;q=0.8');
  headers.set('X-Requested-With', 'XMLHttpRequest');
  if (csrfToken && !headers.has('X-CSRF-Token')) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  if (idempotency && !headers.has('X-Idempotency-Key')) {
    headers.set('X-Idempotency-Key', buildIdempotencyKey());
  }

  const response = await performFetch(`${API_BASE_URL}${safePath}`, {
    ...init,
    headers,
  });

  if (response.status === 401 && auth && retryOnUnauthorized) {
    const refreshed = await refreshAdminSession();

    if (refreshed?.sessionId) {
      return requestBlob(safePath, init, {
        ...options,
        retryOnUnauthorized: false,
      });
    }

    clearStoredAdminSession();
  }

  if (!response.ok) {
    const data = await parseResponseBody(response);
    throw new ApiError(extractMessage(data), response.status, data);
  }

  return response.blob();
}
